// Operator-context shapes (operator-context vertical §6). Mirrors the BFF's
// frozen airports-tree wire (GET /v1/operator-context/airports-tree), which in
// turn mirrors apps/airline AirportsTreeDto / @nexa/airport-reference.

/** A leaf airport node. */
export interface AirportNode {
  /** IATA-3 code. */
  code: string;
  /** Friendly label, e.g. "São Paulo (GRU)". */
  label: string;
}

/** A country grouping of airports. */
export interface CountryNode {
  /** ISO 3166-1 alpha-3 code. */
  code: string;
  label: string;
  airports: AirportNode[];
}

/** A region grouping of countries. */
export interface RegionNode {
  code: string;
  label: string;
  countries: CountryNode[];
}

/** FROZEN tenant airports-tree (region → country → airport). */
export interface AirportsTree {
  regions: RegionNode[];
}

/** Every leaf IATA code under a region. */
export function regionAirports(region: RegionNode): string[] {
  return region.countries.flatMap((c) => c.airports.map((a) => a.code));
}

/** Every leaf IATA code under a country. */
export function countryAirports(country: CountryNode): string[] {
  return country.airports.map((a) => a.code);
}

/**
 * The tree restricted to the operator's airports (`'*'` = unchanged): airports outside the
 * bound are dropped, then countries and regions left empty. Never widens.
 */
export function boundTree(tree: AirportsTree, bound: readonly string[] | '*'): AirportsTree {
  if (bound === '*') return tree;
  const mine = new Set(bound);
  return {
    regions: tree.regions
      .map((region) => ({
        ...region,
        countries: region.countries
          .map((country) => ({
            ...country,
            airports: country.airports.filter((a) => mine.has(a.code)),
          }))
          .filter((country) => country.airports.length > 0),
      }))
      .filter((region) => region.countries.length > 0),
  };
}
